import { Attribute, Relationship, Resource } from 'json-api';
import { Translatable } from 'src/app/translation/translatable';
import { AtomResource } from '../classes/atom-resource';
import { BusinessUnit } from './business-unit.resource';
import { ContentRate } from './content-rate.resource';
import { Currency } from './currency.resource';
import { Firm } from './firm.resource';
import { Lang } from './lang.resource';
import { PhoneFormat, PhoneformatConfig } from './phone-format.resource';
import { Product } from './product.resource';
import { Enum } from './enum.resource';
import { Offer } from './offer.resource';
import { Shortcode } from './shortcode.ressource';
import { AcquisitionAccounts } from './acquisition-account.resource';
import { Notificationmanagementsdvp4m } from './notification.resource';
import { Ussdmenus } from './ussd-menu.ressource';
import { MobileNetworkOperator } from './mobile-network-operator.resource';
import { InternetServiceProvider } from './internet-service-provider.resource';
import {
  StringFilter,
  ResourceListFilter,
  EnumListFilter,
} from 'src/app/filter/filter';
import { AtomService } from '../atom.service';
import { ResourceConfig } from 'src/app/resources/resources-interfaces/resource-config';
import {
  BusinessunitConfig,
  CurrencyConfig,
  LangConfig,
} from 'src/app/shared/tools/search/search-config';
import {
  Dvcontentextendedcountry,
  DvcontentextendedcountryConfig,
} from './dvcontentextendedcountry.resource';

@Resource({
  type: 'countries',
})
export class Country extends AtomResource {
  @Attribute() iso3?: string;
  @Attribute({ key: 'iso_numeric' }) isoNumeric?: number;
  @Attribute() name?: Translatable;
  @Attribute() tld?: string;
  @Attribute() capital?: Translatable;
  @Attribute({ key: 'default_timezone' }) defaultTimezone?: string;
  @Attribute() continent?: string;
  @Attribute() mcc?: number;
  @Attribute({ key: 'calling_code' }) callingCode?: number;
  @Attribute() area?: Enum;
  @Attribute({ key: 'fraud_risk' }) fraudRisk?: Enum;
  @Attribute({ key: 'phone_max_length' }) phoneMaxLength?: number;

  @Relationship() businessunits?: BusinessUnit[];
  @Relationship() contentrates?: ContentRate[];
  @Relationship() currencies?: Currency[];
  @Relationship() firms?: Firm[];
  @Relationship() products?: Product[];
  @Relationship() offers?: Offer[];
  @Relationship() langs?: Lang[];
  @Relationship() defaultlang?: Lang;
  @Relationship() defaultcurrency?: Currency;
  @Relationship() shortcodes?: Shortcode[];
  @Relationship() mobilenetworkoperators?: MobileNetworkOperator[];
  @Relationship() internetserviceproviders?: InternetServiceProvider[];
  @Relationship() acquisitionaccounts?: AcquisitionAccounts[];
  @Relationship() notificationmanagementsdvp4m?: Notificationmanagementsdvp4m[];
  @Relationship() dvcontentextendedcountry?: Dvcontentextendedcountry;
  @Relationship() ussdmenus?: Ussdmenus[];
  @Relationship() phoneformats?: PhoneFormat[];
  @Relationship() enumfraudrisk?: PhoneFormat[];
}

export class CountryConfig implements ResourceConfig {
  uncreatable = true;
  tables = {
    includes: [
      'defaultcurrency',
      'defaultlang',
      'currencies',
      'langs',
      'businessunits',
      'phoneformats.enumformatting',
      'phoneformats.enumtype',
    ],
    columns: [
      {
        columnDef: 'id',
        displayedDatas: [{ path: ['id'], class: 'main', translatable: false }],
        header: 'ID',
        translatable: false,
        displayed: true,
        sort: 'id',
      },
      {
        columnDef: 'name',
        displayedDatas: [
          {
            path: ['name'],
            class: 'mat-body-2',
            translatable: true,
          },
        ],
        header: 'Name',
        displayed: true,
        sort: 'name',
        link: {
          detail: true,
        },
        options: {
          flag: 'id',
        },
      },
      {
        columnDef: 'area',
        displayedDatas: [
          {
            path: ['area'],
            class: 'mat-body-2',
            translatable: false,
          },
        ],
        header: 'Area',
        sort: 'area',
        displayed: true,
      },
      {
        columnDef: 'capital',
        displayedDatas: [
          {
            path: ['capital'],
            class: 'mat-body-2',
            translatable: true,
          },
        ],
        header: 'Capital',
        sort: 'capital',
        displayed: true,
      },
      {
        columnDef: 'defaultlanguage',
        displayedDatas: [
          {
            path: ['defaultlang.name'],
            class: 'mat-body-2',
            translatable: true,
          },
        ],
        header: 'Language',
        sort: 'defaultlang.name',
        displayed: true,
      },
      {
        columnDef: 'defaultcurrency',
        displayedDatas: [
          {
            path: ['defaultcurrency.name'],
            class: 'mat-body-2',
            translatable: true,
          },
        ],
        header: 'Currency',
        sort: 'defaultcurrency.name',
        displayed: true,
      },
      {
        columnDef: 'default_timezone',
        displayedDatas: [
          {
            path: ['defaultTimezone'],
            class: 'mat-body-2',
            translatable: false,
          },
        ],
        header: 'Timezone',
        sort: 'defaultTimezone',
        displayed: true,
      },
      {
        header: 'Firms',
        columnDef: 'firms',
        displayed: true,
        link: {
          resource: 'firms',
          list: 'firms',
          target: 'headquarterscountry',
        },
      },
      {
        columnDef: 'actions',
        header: 'Actions',
        translatable: false,
        displayed: true,
      },
    ],
  };
  detail = {
    header: {
      icon: 'fingerprint',
      options: {
        flag: 'id',
      },
      title: [
        {
          path: ['id'],
          translatable: false,
        },
        {
          path: ['name'],
          translatable: true,
        },
      ],
      subtitle: [
        {
          path: ['area'],
          translatable: false,
        },
      ],
    },
    elements: [
      {
        showEmpty: true,
        columns: true,
        list: [
          {
            icon: 'my_location',
            title: 'Continent',
            APIKey: 'continent',
            value: {
              path: ['continent'],
            },
          },
          {
            icon: 'location_city',
            title: 'Capital',
            APIKey: 'capital',
            value: {
              path: ['capital'],
            },
          },
          {
            icon: 'av_timer',
            title: 'Timezone',
            APIKey: 'defaultTimezone',
            value: {
              path: ['defaultTimezone'],
              translatable: false,
            },
          },
          {
            icon: 'phishing',
            title: 'Fraud Risk',
            APIKey: 'fraud_risk',
            value: {
              path: ['enumfraudrisk.label'],
              translatable: false,
            },
          },
        ],
      },
    ],
    tabs: [
      {
        title: 'General ',
        elements: [
          {
            title: 'Business Unit',
            list: [
              {
                type: 'chips',
                APIKey: 'businessunits',
                value: {
                  path: ['name'],
                  translatable: true,
                },
              },
            ],
          },
          {
            title: 'Language',
            list: [
              {
                type: 'chips',
                APIKey: 'langs',
                value: {
                  path: ['name'],
                  translatable: true,
                },
                options: {
                  showDefault: {
                    APIKey: 'defaultlang',
                  },
                },
              },
            ],
          },
          {
            title: 'Currencies',
            list: [
              {
                type: 'chips',
                APIKey: 'currencies',
                value: {
                  path: ['name'],
                  translatable: true,
                },
                options: {
                  showDefault: {
                    APIKey: 'defaultcurrency',
                  },
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Standard',
        elements: [
          {
            showEmpty: true,
            columns: true,
            list: [
              {
                icon: 'travel_explore',
                title: 'Area',
                APIKey: 'area',
                value: {
                  path: ['area'],
                  translatable: false,
                },
              },
              {
                icon: 'smartphone',
                title: 'MCC',
                APIKey: 'mcc',
                value: {
                  path: ['mcc'],
                  translatable: false,
                },
              },
              {
                icon: 'phone',
                title: 'Calling code',
                APIKey: 'callingCode',
                value: {
                  path: ['callingCode'],
                  translatable: false,
                },
              },
              {
                icon: 'label',
                title: 'ISO2',
                APIKey: 'id',
                value: {
                  path: ['id'],
                  translatable: false,
                },
              },
              {
                icon: 'label',
                title: 'ISO3',
                APIKey: 'iso3',
                value: {
                  path: ['iso3'],
                  translatable: false,
                },
              },
              {
                icon: 'http',
                title: 'TLD',
                APIKey: 'tld',
                value: {
                  path: ['tld'],
                  translatable: false,
                },
              },
            ],
          },
        ],
      },
      {
        title: 'Phone Format',
        elements: [
          {
            resource: {
              APIKey: 'phoneformats',
              config: new PhoneformatConfig(),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'Linked resources',
        elements: [
          {
            showEmpty: true,
            columns: true,
            list: [
              {
                icon: 'business',
                title: 'Firms',
                APIKey: 'firms',
                value: {
                  path: ['firms'],
                  translatable: false,
                },
                link: {
                  resource: 'firms',
                  list: 'firms',
                  target: 'headquarterscountry',
                },
              },
              {
                icon: 'public',
                title: 'Products',
                APIKey: 'products',
                value: {
                  path: ['products'],
                  translatable: false,
                },
                link: {
                  resource: 'products',
                  list: 'products',
                  target: 'country',
                },
              },
              {
                icon: 'local_offer',
                title: 'Offers',
                APIKey: 'offers',
                value: {
                  path: ['offers'],
                  translatable: false,
                },
                link: {
                  resource: 'offers',
                  list: 'offers',
                  target: 'country',
                },
              },
              {
                icon: 'dialpad',
                title: 'Shortcodes',
                APIKey: 'shortcodes',
                value: {
                  path: ['shortcodes'],
                  translatable: false,
                },
                link: {
                  resource: 'shortcodes',
                  list: 'shortcodes',
                  target: 'country',
                },
              },
              {
                icon: 'fingerprint',
                title: 'Acquisition Accounts',
                APIKey: 'acquisitionaccounts',
                value: {
                  path: ['acquisitionaccounts'],
                  translatable: false,
                },
                link: {
                  resource: 'acquisitionaccounts',
                  list: 'acquisitionaccounts',
                  target: 'country',
                },
              },
              {
                icon: 'list',
                title: 'Ussd Menus',
                APIKey: 'ussdmenus',
                value: {
                  path: ['ussdmenus'],
                  translatable: false,
                },
                link: {
                  resource: 'ussdmenus',
                  list: 'ussdmenus',
                  target: 'country',
                },
              },
            ],
          },
        ],
      },
      {
        title: 'DV Content',
        elements: [
          {
            resource: {
              APIKey: 'dvcontentextendedcountry',
              config: new DvcontentextendedcountryConfig(),
              multiple: false,
            },
          },
        ],
      },
    ],
  };
  wizard = {
    tabs: [
      {
        title: 'Main Settings',
        formGroup: 'main',
        list: [
          {
            type: 'text',
            title: 'Name',
            formControl: 'name',
            APIKey: 'name',
            translatable: true,
            required: true,
          },
          {
            type: 'text',
            title: 'continent',
            formControl: 'continent',
            APIKey: 'continent',
            translatable: false,
            required: true,
          },
          {
            type: 'text',
            title: 'Capital',
            formControl: 'capital',
            APIKey: 'capital',
            translatable: true,
            required: true,
          },
          {
            type: 'text',
            title: 'Timezone',
            formControl: 'defaultTimezone',
            APIKey: 'default_timezone',
            multiple: false,
            required: false,
          },
          {
            type: 'enum',
            title: 'Fraud Risk',
            formControl: 'fraudRisk',
            APIKey: 'fraudRisk',
            fieldKey: 'fraud_risk',
            translatable: false,
            required: false,
          },
        ],
      },
      {
        title: 'General',
        formGroup: 'general',
        list: [
          {
            type: 'resource',
            title: 'Business Unit',
            formControl: 'businessunits',
            APIKey: 'businessunits',
            multiple: true,
            required: false,
            search: new BusinessunitConfig(true),
          },
          {
            type: 'resource',
            title: 'Lang',
            formControl: 'langs',
            search: new LangConfig(this.atom, true),
            required: true,
            multiple: true,
            options: {
              defaultValue: 'defaultlang',
            },
          },
          {
            type: 'resource',
            title: 'Currency',
            formControl: 'currencies',
            search: new CurrencyConfig(true),
            required: true,
            multiple: true,
            options: {
              defaultValue: 'defaultcurrency',
            },
          },
        ],
      },
      {
        title: 'standard',
        formGroup: 'standard',
        list: [
          {
            type: 'enum',
            title: 'Area',
            formControl: 'area',
            APIKey: 'area',
            fieldKey: 'area',
            translatable: false,
            required: true,
          },
          {
            type: 'text',
            title: 'MCC',
            formControl: 'mcc',
            APIKey: 'mcc',
            translatable: false,
            required: true,
          },
          {
            type: 'text',
            title: 'Calling Code',
            formControl: 'callingCode',
            translatable: false,
            required: true,
          },
          {
            type: 'text',
            title: 'ISO3',
            formControl: 'iso3',
            translatable: false,
            required: true,
          },
          {
            type: 'text',
            title: 'TLD',
            formControl: 'tld',
            translatable: false,
            required: true,
          },
        ],
      },
      {
        title: 'Phone Format',
        formGroup: 'phoneformat',
        list: [
          {
            type: 'ownResource',
            required: false,
            resource: {
              APIKey: 'phoneformats',
              config: new PhoneformatConfig(),
              multiple: true,
            },
          },
        ],
      },
      {
        title: 'DV Content',
        formGroup: 'dvcontent',
        list: [
          {
            type: 'ownResource',
            required: false,
            resource: {
              APIKey: 'dvcontentextendedcountry',
              config: new DvcontentextendedcountryConfig(),
              multiple: false,
            },
          },
        ],
      },
    ],
  };
  resource = Country;
  resourceName = 'Country';
  resourceKey = 'countries';
  resourceKeySingle = 'country';
  includes = [
    'defaultcurrency',
    'defaultlang',
    'currencies',
    'langs',
    'businessunits',
    'phoneformats.enumformatting',
    'phoneformats.enumtype',
    'dvcontentextendedcountry',
    'enumfraudrisk',
  ];
  filters = [
    new StringFilter('id', 'ID', true),
    new StringFilter('name', 'Name'),
    new EnumListFilter(this.atom, 'countries', 'area', 'area', 'Area'),
    new StringFilter('continent', 'Continent'),
    new StringFilter('currencies.id', 'Currency ID'),
    new StringFilter('currencies.name', 'Currency NAME'),
    new ResourceListFilter(
      this.atom,
      BusinessUnit,
      'businessunits.id',
      'Business unit'
    ),
  ];
  constructor(private atom: AtomService) {}
}
/*
countries

fields:

id: string, length 2, required
iso3: string, length 3, required
iso_numeric: integer, required
name: translatable, required
tld: string, max:50
capital: translatable
default_timezone: string, max:50
continent: string, max:50, required
mcc: integer
calling_code: integer
area: enum, required
fraud_risk: enum
relations:

defaultarpus: oneToMany on defaultarpus
mobilenetworkoperators: oneToMany on mobilenetworkoperators
internetserviceproviders: oneToMany on internetserviceproviders
dvcontentextendedcountry: oneToOne on dvcontentextendedcountry
ussdmenus: oneToMany on ussdmenus
dvcontentextendedcountry

fields:

id: integer, required
gtm_container_id: string, length 11, start with: GTM-
ga_measurment_id: string, length 12, start with: G-
didomi_consent_id: string, length 8
relations:

country: oneToOne on countries, required
langs

fields:

id: string, length 2, required
name: translatable, required
direction: enum, required
relations:
countries: manyToMany on countries
ussdmenus: manyToMany on ussdmenus
currencies

fields:

id: string, length 3, required
name: translatable, required
relations:

countries: ManyToMany on countries
exchangerates: oneToMany on exchangerates
baseexchangerates: oneToMany on exchangerates
skus: oneToMany on skus
offers: oneToMany on offers
orderedsequencerules: oneToMany on orderedsequencerules
pricepointrules: oneToMany on pricepointrules
acquisitionaccounts: oneToMany on acquisitionaccounts
phoneformats

fields:

id: integer, required
regex: string, required
type: enum, required
formatting: enum, required
relations:

countries: ManyToOne on countries, required
*/
